// Considered: the own-properties row's open body (three `TypeFieldRowMolecule` controls —
// name, type, required) — rejected. The contract's `<PropertyEditor>`
// (`studio/type-property.jsx:243-540`) is a nine-section sheet of 79 datums: what it is,
// what it points at, the structured shape, the choices, the units, how it behaves, the
// checks, how it renders and who can see it. A row cannot hold it, and the spec's Reuse
// Audit records the decision (`PropertyEditorMolecule`, "the row is name + type in one
// line").
//
// Considered: `DialogWrapper` — rejected for the same reason `RuleSheetMolecule` rejected
// it: the contract opens the editor IN PLACE beneath the property's row, and a portalled
// dialog puts every datum outside the composer's `own-properties` container.
import React, { useId, useMemo } from 'react';
import { useTranslation } from 'localization';
import type { RendererMap } from 'types/aspect';
import type { PropertyRule, SchemaProperty, SchemaPropertyPermission, UniqueScope } from 'types/schema';
import SegmentedControlAtom from '../../atoms/SegmentedControl/SegmentedControlAtom';
import type { FilterGroup, UnifiedFilters } from 'types/filters';
import type { PropertyOption } from '../PropertyFilter/PropertyFilterMolecule';
import PropertyFilterBuilderMolecule from './PropertyFilterBuilderMolecule';
import { PROPERTY_FILTER_PARITY } from './propertyFilterParity';
import InputAtom from '../../atoms/Input/InputAtom';
import DropdownAtom from '../../atoms/Dropdown/DropdownAtom';
import './PropertyEditorMolecule.scss';

const KEY = 'studio.typeEditor.properties.editor';

/** `PROP_TYPES` — the vocabulary the contract offers, in its order. */
export const PROP_TYPES = ['string', 'number', 'boolean', 'date', 'datetime', 'array', 'json', 'unit', 'reference'] as const;

/**
 * `RULE_METHODS` — per-property checks only, named as the platform names them.
 * `minLength` / `maxLength` / `pattern` are real aliases the validation builder handles
 * explicitly and `precision` has bespoke handling; everything else dispatches to the Yup
 * method of the same name. A name that is neither looks saved and never runs, which is why
 * the editor offers this closed set rather than free text.
 */
export const RULE_METHODS: {
  id: string;
  arg: 'number' | 'text' | 'list' | null;
  types?: string[];
}[] = [
  { id: 'minLength', arg: 'number', types: ['string'] },
  { id: 'maxLength', arg: 'number', types: ['string'] },
  { id: 'pattern', arg: 'text', types: ['string'] },
  { id: 'email', arg: null, types: ['string'] },
  { id: 'url', arg: null, types: ['string'] },
  { id: 'min', arg: 'number', types: ['number', 'unit', 'date', 'datetime'] },
  { id: 'max', arg: 'number', types: ['number', 'unit', 'date', 'datetime'] },
  { id: 'integer', arg: null, types: ['number'] },
  { id: 'positive', arg: null, types: ['number'] },
  { id: 'precision', arg: 'number', types: ['number', 'unit'] },
  { id: 'oneOf', arg: 'list', types: ['string', 'number'] },
];

const ruleDef = (id: string) => RULE_METHODS.find((method) => method.id === id);

/**
 * What a schema primitive is CALLED — `Text`, `Number`, `Date & time` — wherever a
 * property's type is read (the row's type chip, the editor's Type select). A type the
 * vocabulary does not know reads as its own token: an identifier is a truer answer than
 * a missing key.
 */
export function propTypeLabel(type: string | undefined, t: (key: string) => string): string {
  if (!type) return '';
  return (PROP_TYPES as readonly string[]).includes(type) ? t(`${KEY}.propType.${type}`) : type;
}

/**
 * `args` is an array on the wire, spread into the validator. `oneOf` takes the whole set as
 * ONE argument, so its args nest one level — a flat list would read as N separate arguments.
 */
export function toArgs(method: string, raw: string): unknown[] {
  const def = ruleDef(method);
  if (!def?.arg) return [];
  if (def.arg === 'number') return raw === '' ? [] : [Number(raw)];
  if (def.arg === 'list') {
    const items = String(raw ?? '')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
    return items.length ? [items] : [];
  }
  return raw ? [raw] : [];
}

export function fromArgs(method: string, args: unknown[] | undefined): string {
  const def = ruleDef(method);
  if (!def?.arg || !Array.isArray(args) || !args.length) return '';
  if (def.arg === 'list') return (Array.isArray(args[0]) ? (args[0] as unknown[]) : args).join(', ');
  return String(args[0]);
}

/**
 * The checks as one sentence — `at least 3 characters · at most 40 characters` — for a row
 * that has no description to show. Only checks the editor knows how to name are read; a
 * stale method is the editor's business (`data-bad`), not the row's.
 */
export function describePropertyRules(
  rules: { method: string; args?: unknown[] }[] | undefined,
  t: (key: string, params?: Record<string, unknown>) => string
): string {
  return (rules ?? [])
    .filter((rule) => ruleDef(rule.method))
    .map((rule) => t(`${KEY}.ruleSentence.${rule.method}`, { value: fromArgs(rule.method, rule.args) }))
    .join(' · ');
}

/** `category` is required on a measured value: without it there is no family to convert within. */
export const UNIT_CATEGORIES = ['time', 'length', 'distance', 'mass', 'speed', 'volume', 'temperature', 'area'] as const;

/** What happens to values already recorded under the old type. Publish is refused without one. */
export const MIGRATIONS = ['coerce', 'require_revalidation', 'new_identity'] as const;

const PERMS: SchemaPropertyPermission[] = ['discover', 'view', 'edit', 'delete', 'manage_security', 'organization_user', 'impersonate', 'manage'];

/** Renderers only — the `editComponent` family is legacy and deliberately absent. */
const RENDERERS: Record<string, string[]> = {
  string: ['TextField', 'TextArea', 'ChoiceChips', 'Select'],
  number: ['NumberField', 'Stepper', 'Slider'],
  boolean: ['Switch', 'Checkbox'],
  date: ['DatePicker', 'DateChip'],
  datetime: ['DateTimePicker'],
  array: ['TagList', 'OrderedList'],
  json: ['KeyValueTable'],
  unit: ['UnitField'],
  reference: ['ReferencePicker', 'ReferenceCard'],
};

/**
 * What a structured value's shape can say — the validator's subset exactly
 * (`generateYupValidationSchema`'s `applyJsonSchemaValidation`): one level of typed fields
 * with a handful of constraints, not JSON Schema at large. Offering more would let someone
 * write a shape the platform quietly ignores.
 */
export const STRUCT_TYPES: {
  id: string;
  args: [string, 'number' | 'text'][];
  note?: boolean;
}[] = [
  {
    id: 'string',
    args: [
      ['pattern', 'text'],
      ['minLength', 'number'],
      ['maxLength', 'number'],
    ],
  },
  {
    id: 'number',
    args: [
      ['minimum', 'number'],
      ['maximum', 'number'],
    ],
  },
  {
    id: 'integer',
    args: [
      ['minimum', 'number'],
      ['maximum', 'number'],
    ],
  },
  { id: 'boolean', args: [] },
  {
    id: 'array',
    args: [
      ['minItems', 'number'],
      ['maxItems', 'number'],
    ],
    note: true,
  },
  { id: 'object', args: [], note: true },
];

const structType = (id: string | undefined) => STRUCT_TYPES.find((entry) => entry.id === id) ?? STRUCT_TYPES[0];

export const machineKey = (value: string): string =>
  String(value ?? '')
    .toLowerCase()
    .replaceAll(/[^a-z0-9]+/g, '_')
    .replaceAll(/^_+|_+$/g, '');

/** One field of a structured value's shape, flattened out of the stored JSON Schema. */
export interface StructureField {
  name: string;
  type?: string;
  required?: boolean;
  [constraint: string]: unknown;
}

export function fieldsOf(jsonSchema: Record<string, unknown> | null | undefined): StructureField[] {
  const properties = (jsonSchema?.properties ?? {}) as Record<string, Record<string, unknown>>;
  const required = (jsonSchema?.required ?? []) as string[];
  return Object.entries(properties).map(([name, def]) => ({
    name,
    ...(def ?? {}),
    required: required.includes(name),
  }));
}

/**
 * The stored artifact is real JSON Schema, so a developer reading it sees what the validator
 * reads. `base` carries through anything a migration put there that this editor does not
 * model — dropping it on save would rewrite someone else's artifact.
 */
export function toSchema(title: string | undefined, fields: StructureField[], base: Record<string, unknown> | null | undefined): Record<string, unknown> {
  return {
    ...(base ?? {}),
    type: 'object',
    ...(title ? { title } : {}),
    properties: fields.reduce<Record<string, unknown>>((acc, field) => {
      const { name, required: _required, ...rest } = field;
      if (name) acc[name] = rest;
      return acc;
    }, {}),
    required: fields.filter((field) => field.required && field.name).map((field) => field.name),
  };
}

export interface PropertyEditorMoleculeProps {
  /** The property being authored. Patches come back through `onChange`. */
  property: SchemaProperty;
  /** The type the property is declared on — the head says where the reader is. */
  typeName: string;
  /** An inherited or aspect-contributed property is read here, never edited. */
  readOnly?: boolean;
  /**
   * `true` once spheres of this type exist under this property, which makes a rename a
   * migration rather than an edit. The key follows the label only until then.
   */
  locked?: boolean;
  /**
   * The type this property carried when it was last published. Set only when it is known —
   * a change away from it is the migration question, and asking it from a guess would
   * demand a migration for a type that never shipped.
   */
  publishedType?: string;
  /** The concrete types a reference property can point at. */
  targetTypes?: { systemName: string; name: string }[];
  /**
   * The catalogue the `Narrow it` builder may point a condition at — the target type's own
   * properties. Omitted, the section says the builder is unavailable rather than offering an
   * empty picker: a condition with nothing to point at cannot be authored.
   */
  filterProperties?: PropertyOption[];
  /** Initial-state seams for the filter builder, so a story can capture one of its states. */
  initialFilterAdding?: boolean;
  initialFilterExpandedRowId?: string;
  initialFilterPickingRowId?: string;
  onChange?: (patch: Partial<SchemaProperty>) => void;
  /**
   * The edit is finished and worth sending. Text inputs raise it on blur — a keystroke is
   * not a save — and every other control raises it with its own change, because a toggle
   * has no blur that means anything.
   */
  onCommit?: () => void;
  onClose?: () => void;
  /** Absent on a read-only editor, and on a row whose delete is confirmed elsewhere. */
  onDelete?: () => void;
  className?: string;
}

const PropertyEditorMolecule: React.FC<PropertyEditorMoleculeProps> = ({
  property,
  typeName,
  readOnly = false,
  locked = false,
  publishedType,
  targetTypes = [],
  filterProperties,
  initialFilterAdding,
  initialFilterExpandedRowId,
  initialFilterPickingRowId,
  onChange,
  onCommit,
  onClose,
  onDelete,
  className,
}) => {
  const { t } = useTranslation();
  /** One id namespace per mounted editor, so two open rows never collide. */
  const uid = useId();
  const fieldId = (control: string) => `${uid}-${control}`;
  const set = (patch: Partial<SchemaProperty>) => onChange?.(patch);
  /**
   * The renderer selects author the canonical `RendererMap` (`types/aspect`): a per-mode map
   * of `RendererRef`s, not of names. The two the contract exposes are the platform's `edit`
   * and `view` modes — the contract's word for `view` is "Reading" — and every name the type
   * offers is a built-in, so the ref's origin is always `catalog`. Clearing a mode drops its
   * key rather than storing an empty ref, so `resolveRenderer` falls back to the type default.
   */
  const rendererMapWith = (mode: 'edit' | 'view', name: string): RendererMap | undefined => {
    const next: RendererMap = { ...property.renderers };
    if (name) next[mode] = { origin: 'catalog', name };
    else delete next[mode];
    return Object.keys(next).length ? next : undefined;
  };
  /** A change that is complete the instant it is made, so it is sent with it. */
  const setNow = (patch: Partial<SchemaProperty>) => {
    onChange?.(patch);
    onCommit?.();
  };
  const commit = () => onCommit?.();
  const metadata = (property.metadata ?? {}) as Record<string, unknown>;
  const setMeta = (patch: Record<string, unknown>) => set({ metadata: { ...metadata, ...patch } });

  /**
   * The `Narrow it` condition, read out of and written back into the canonical
   * `UnifiedFilters` the property already persists. The builder edits ONE and/or tree, which
   * is `filterGroups[0]`; nothing else on the filter is touched, so a filter carrying lanes
   * this builder does not author survives a round trip.
   */
  const targetFilter = (metadata.targetFilter ?? {}) as UnifiedFilters;
  const targetFilterGroup: FilterGroup = targetFilter.filterGroups?.[0] ?? {
    operator: 'and',
    expressions: [],
  };
  const setTargetFilterGroup = (next: FilterGroup) =>
    setMeta({
      targetFilter: { ...targetFilter, filterGroups: [next, ...(targetFilter.filterGroups ?? []).slice(1)] },
    });
  const unit = property.unitConfig ?? ({} as NonNullable<SchemaProperty['unitConfig']>);
  const setUnit = (patch: Record<string, unknown>) => set({ unitConfig: { ...unit, ...patch } } as Partial<SchemaProperty>);
  const renderers = RENDERERS[property.type] ?? [];
  const options = (metadata.options ?? []) as { label: string; value: string }[];
  const jsonSchema = (property.jsonSchema ?? null) as Record<string, unknown> | null;
  const fields = useMemo(() => fieldsOf(jsonSchema), [jsonSchema]);
  const rules = property.rules ?? [];
  /**
   * A published type only exists for a property that has been published once, so the
   * migration question appears exactly when it is real.
   */
  const typeChanged = !!publishedType && publishedType !== property.type;
  /** Only the checks that mean something for this type — see `RULE_METHODS`. */
  const usable = RULE_METHODS.filter((method) => !method.types || method.types.includes(property.type));
  const hasStaleRule = rules.some((rule) => !usable.some((method) => method.id === rule.method));

  const writeFields = (title: string | undefined, next: StructureField[]) => set({ jsonSchema: toSchema(title, next, jsonSchema) });
  const writeFieldsNow = (title: string | undefined, next: StructureField[]) => setNow({ jsonSchema: toSchema(title, next, jsonSchema) });
  const updateField = (index: number, patch: Partial<StructureField>) =>
    writeFields(
      jsonSchema?.title as string | undefined,
      fields.map((field, i) => (i === index ? { ...field, ...patch } : field))
    );
  const updateRule = (index: number, patch: Partial<PropertyRule>) => set({ rules: rules.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)) });

  /** `<CSection>` — a titled band with an optional note, the contract's own grouping. */
  const section = (headingDatum: string, label: string, note: string | undefined, children: React.ReactNode, testId: string) => (
    <div className="prop-editor__section" data-testid={testId}>
      <div className="prop-editor__section-head">
        <h3 data-datum={headingDatum} data-kind="heading">
          {label}
        </h3>
        {note && <i>{note}</i>}
      </div>
      {children}
    </div>
  );

  /** `<CSelect>` — the contract's select, rendered through the platform's dropdown atom. */
  const select = (
    datum: string,
    value: string,
    ariaLabel: string,
    entries: { id: string; label: string }[],
    onSelect: (value: string) => void,
    testId: string
  ) => (
    <DropdownAtom
      className="prop-editor__select"
      dropdownSize="small"
      // `inputId`, not `id`: PrimeReact puts `id` on the root <div> and `inputId` on the
      // hidden focusable <input>. The seven <label htmlFor> above point at the control the
      // user actually focuses, so an `id` here would leave every one of them orphaned —
      // the label would name nothing and clicking it would not open the dropdown.
      inputId={fieldId(testId)}
      value={value}
      disabled={readOnly}
      aria-label={ariaLabel}
      data-datum={datum}
      data-kind="select"
      data-testid={testId}
      optionLabel="label"
      optionValue="value"
      options={entries.map((entry) => ({ label: entry.label, value: entry.id }))}
      onChange={(event) => {
        onSelect(event.value as string);
        onCommit?.();
      }}
    />
  );

  /** `<label class="p-check">` — the wrapper carries the contract's label datum, the input its own.
   *  The box stays a native `<input type="checkbox">`: the `checkbox` atom renders its own label
   *  and wrapper, forwards no `data-*` and takes no `disabled`, so routing through it would drop
   *  both the datum the parity walker matches on and the read-only state. */
  const check = (labelDatum: string, inputDatum: string, label: string, checked: boolean, onToggle: (checked: boolean) => void, testId: string) => (
    <label className="prop-editor__check" data-datum={labelDatum} data-kind="checkbox">
      <input
        type="checkbox"
        checked={checked}
        disabled={readOnly}
        data-datum={inputDatum}
        data-kind="checkbox"
        data-testid={testId}
        onChange={(event) => {
          onToggle(event.target.checked);
          onCommit?.();
        }}
      />
      {label}
    </label>
  );

  const typeLabel = (id: string | undefined) => propTypeLabel(id, t);

  return (
    <div
      className={['prop-editor', className].filter(Boolean).join(' ')}
      data-testid="property-editor"
      role="group"
      aria-label={property.label || t('studio.typeEditor.properties.newProperty')}
    >
      <div className="prop-editor__head">
        <h2 className="prop-editor__title" data-datum="props.new-property#2" data-kind="heading">
          <b data-datum="props.new-property#3" data-kind="strong">
            {property.label || t('studio.typeEditor.properties.newProperty')}
          </b>
        </h2>
        <span className="prop-editor__hint" data-datum="props.on" data-kind="hint">
          {t(`${KEY}.on`, { typeName })}
        </span>
        {!readOnly && onDelete && (
          <button
            type="button"
            className="prop-editor__icon"
            title={t(`${KEY}.deleteThisProperty`)}
            aria-label={t(`${KEY}.deleteThisProperty`)}
            data-datum="props.delete-this-property"
            data-kind="button"
            data-testid="property-editor-delete"
            onClick={onDelete}
          >
            <i className="pi pi-trash" aria-hidden="true" />
          </button>
        )}
        <button type="button" className="prop-editor__quiet" data-datum="props.done" data-kind="button" data-testid="property-editor-done" onClick={onClose}>
          {t(`${KEY}.done`)}
        </button>
      </div>

      {section(
        'props.label',
        t(`${KEY}.whatItIs`),
        undefined,
        <>
          <div className="prop-editor__row">
            <label className="prop-editor__label" htmlFor={fieldId('property-editor-label')} data-datum="props.label#2" data-kind="label">
              {t(`${KEY}.labelField`)}
            </label>
            <InputAtom
              className="prop-editor__input"
              value={property.label ?? ''}
              readOnly={readOnly}
              placeholder={t(`${KEY}.labelPlaceholder`)}
              aria-label={t(`${KEY}.labelField`)}
              data-datum="props.vendor-code"
              onBlur={commit}
              data-kind="input"
              data-testid="property-editor-label"
              id={fieldId('property-editor-label')}
              onChange={(event) =>
                set({
                  label: event.target.value,
                  // The key follows the label only until publish; identity is the id.
                  ...(locked ? {} : { name: machineKey(event.target.value) }),
                })
              }
            />
            <span className="prop-editor__hint" data-datum="props.renaming-after-spheres-exist-is-a-migrat" data-kind="hint">
              {locked ? t(`${KEY}.renameLocked`) : t(`${KEY}.renameFree`)}
            </span>
          </div>
          <div className="prop-editor__row">
            <label className="prop-editor__label" htmlFor={fieldId('property-editor-note')} data-datum="props.help-text" data-kind="label">
              {t(`${KEY}.helpText`)}
            </label>
            <InputAtom
              className="prop-editor__input prop-editor__input--wide"
              value={property.description ?? ''}
              readOnly={readOnly}
              placeholder={t(`${KEY}.helpTextPlaceholder`)}
              aria-label={t(`${KEY}.helpText`)}
              data-datum="props.what-it-holds-and-who-fills-it-in"
              onBlur={commit}
              data-kind="input"
              data-testid="property-editor-note"
              id={fieldId('property-editor-note')}
              onChange={(event) => set({ description: event.target.value })}
            />
          </div>
          <div className="prop-editor__row">
            <label className="prop-editor__label" htmlFor={fieldId('property-editor-type')} data-datum="props.type" data-kind="label">
              {t(`${KEY}.typeField`)}
            </label>
            {select(
              'props.select',
              property.type,
              t(`${KEY}.typeField`),
              PROP_TYPES.map((id) => ({ id, label: typeLabel(id) })),
              (value) =>
                set({
                  type: value,
                  renderers: undefined,
                  metadata: {},
                  unitConfig: undefined,
                  rules: [],
                }),
              'property-editor-type'
            )}
            <span className="prop-editor__hint" data-datum="props.picks-the-default-renderer-and-what-can-" data-kind="hint">
              {t(`${KEY}.typeHint`)}
              {property.type === 'string' && options.length > 0 ? t(`${KEY}.typeHintChoices`) : ''}
            </span>
          </div>
          {typeChanged && (
            <>
              <div className="prop-editor__row">
                <span className="prop-editor__label" data-datum="props.values-already-held" data-kind="label">
                  {t(`${KEY}.valuesAlreadyHeld`)}
                </span>
                <SegmentedControlAtom
                  mode="single"
                  size="small"
                  renderAs="button"
                  optionKind="button"
                  ariaLabel={t(`${KEY}.valuesAlreadyHeld`)}
                  value={property.migration ?? ''}
                  options={MIGRATIONS.map((id) => ({
                    value: id,
                    label: t(`${KEY}.migration.${id}`),
                    datum: 'props.o-label',
                    disabled: readOnly,
                  }))}
                  onChange={(value) => setNow({ migration: value as SchemaProperty['migration'] })}
                  data-testid="property-editor-migration"
                />
              </div>
              <p className="prop-editor__note" data-datum="props.this-was-published-as" data-kind="note">
                {property.migration ? t(`${KEY}.migrationNote.${property.migration}`) : t(`${KEY}.migrationUnanswered`, { type: typeLabel(publishedType) })}
              </p>
            </>
          )}
        </>,
        'property-editor-what-it-is'
      )}

      {property.type === 'reference' &&
        section(
          'props.label#3',
          t(`${KEY}.whatItPointsAt`),
          undefined,
          <>
            <div className="prop-editor__row">
              <label className="prop-editor__label" htmlFor={fieldId('property-editor-target-type')} data-datum="props.target-type" data-kind="label">
                {t(`${KEY}.targetType`)}
              </label>
              {select(
                'props.select#2',
                (metadata.targetSchemaSystemName as string) ?? '',
                t(`${KEY}.targetType`),
                [{ id: '', label: t(`${KEY}.chooseOne`) }, ...targetTypes.map((type) => ({ id: type.systemName, label: type.name }))],
                (value) => setMeta({ targetSchemaSystemName: value || undefined }),
                'property-editor-target-type'
              )}
              <span className="prop-editor__hint" data-datum="props.required-a-reference-with-no-target-can-" data-kind="hint">
                {t(`${KEY}.targetTypeHint`)}
              </span>
            </div>
            <div className="prop-editor__row">
              <label className="prop-editor__label" htmlFor={fieldId('property-editor-display-property')} data-datum="props.shows" data-kind="label">
                {t(`${KEY}.shows`)}
              </label>
              <InputAtom
                className="prop-editor__input"
                value={(metadata.displayProperty as string) ?? 'name'}
                readOnly={readOnly}
                aria-label={t(`${KEY}.shows`)}
                data-datum="props.input"
                onBlur={commit}
                data-kind="input"
                data-testid="property-editor-display-property"
                id={fieldId('property-editor-display-property')}
                onChange={(event) => setMeta({ displayProperty: event.target.value })}
              />
              <span className="prop-editor__hint" data-datum="props.which-property-of-the-target-a-person-re" data-kind="hint">
                {t(`${KEY}.showsHint`)}
              </span>
            </div>
            {/*
              This one really is a filter — unlike a perspective's eligible-assignee set,
              which only looks like one. The builder itself is the composer's
              `UnifiedFilterEditor`, mounted by the caller so this editor stays free of the
              filter model; without it the section says so rather than pretending.
            */}
            <div className="prop-editor__filter">
              <span className="prop-editor__label" data-datum="props.narrow-it" data-kind="label">
                {t(`${KEY}.narrowIt`)}
              </span>
              {filterProperties?.length ? (
                <PropertyFilterBuilderMolecule
                  value={targetFilterGroup}
                  onChange={setTargetFilterGroup}
                  availableProperties={filterProperties}
                  readOnly={readOnly}
                  parity={PROPERTY_FILTER_PARITY}
                  title={t(`${KEY}.filter.title`)}
                  emptyNote={t(`${KEY}.filter.emptyNote`)}
                  footnote={t(`${KEY}.filter.footnote`)}
                  initialAdding={initialFilterAdding}
                  initialExpandedRowId={initialFilterExpandedRowId}
                  initialPickingRowId={initialFilterPickingRowId}
                />
              ) : (
                <span className="prop-editor__hint" data-datum="props.filter-builder-unavailable" data-kind="hint">
                  {t(`${KEY}.filterBuilderUnavailable`)}
                </span>
              )}
            </div>
          </>,
          'property-editor-what-it-points-at'
        )}

      {property.type === 'json' &&
        section(
          'props.label#5',
          t(`${KEY}.shape`),
          t(`${KEY}.shapeNote`),
          <div className="prop-editor__struct" data-testid="property-editor-structure">
            <div className="prop-editor__row">
              <label className="prop-editor__label" htmlFor={fieldId('property-editor-struct-title')} data-datum="props.called" data-kind="label">
                {t(`${KEY}.called`)}
              </label>
              <InputAtom
                className="prop-editor__input"
                value={(jsonSchema?.title as string) ?? ''}
                readOnly={readOnly}
                placeholder={t(`${KEY}.calledPlaceholder`)}
                aria-label={t(`${KEY}.called`)}
                data-datum="props.schedule-definition"
                onBlur={commit}
                data-kind="input"
                data-testid="property-editor-struct-title"
                id={fieldId('property-editor-struct-title')}
                onChange={(event) => writeFields(event.target.value, fields)}
              />
              <span className="prop-editor__hint" data-datum="props.the-heading-a-person-sees-above-the-fiel" data-kind="hint">
                {t(`${KEY}.calledHint`)}
              </span>
            </div>
            {fields.map((field, index) => {
              const struct = structType(field.type);
              return (
                <div className="prop-editor__field" key={`${field.name}-${index}`} data-container="each(fields)">
                  <div className="prop-editor__field-top">
                    <InputAtom
                      className="prop-editor__input"
                      value={field.name ?? ''}
                      readOnly={readOnly}
                      placeholder={t(`${KEY}.fieldName`)}
                      aria-label={t(`${KEY}.fieldName`)}
                      data-datum="props.field-name"
                      onBlur={commit}
                      data-kind="input"
                      data-testid={`property-editor-field-name-${index}`}
                      onChange={(event) => updateField(index, { name: machineKey(event.target.value) })}
                    />
                    {select(
                      'props.select#5',
                      field.type ?? 'string',
                      t(`${KEY}.fieldType`),
                      STRUCT_TYPES.map((entry) => ({
                        id: entry.id,
                        label: t(`${KEY}.structType.${entry.id}`),
                      })),
                      (value) =>
                        writeFields(
                          jsonSchema?.title as string | undefined,
                          fields.map((entry, i) => (i === index ? { name: entry.name, required: entry.required, type: value } : entry))
                        ),
                      `property-editor-field-type-${index}`
                    )}
                    {check(
                      'props.required#2',
                      'props.checkbox',
                      t(`${KEY}.required`),
                      !!field.required,
                      (checked) => updateField(index, { required: checked }),
                      `property-editor-field-required-${index}`
                    )}
                    {struct.args.map(([constraint, kind]) => (
                      <span className="prop-editor__farg" key={constraint} data-container="each(fields) > each(args)" data-datum="props.text#9" data-kind="text">
                        <span className="prop-editor__hint" data-datum="props.label#6" data-kind="hint">
                          {t(`${KEY}.structArg.${constraint}`)}
                        </span>
                        <InputAtom
                          className="prop-editor__input prop-editor__input--narrow"
                          value={field[constraint] === undefined ? '' : String(field[constraint])}
                          readOnly={readOnly}
                          aria-label={t(`${KEY}.structArg.${constraint}`)}
                          data-datum="props.input#2"
                          onBlur={commit}
                          data-kind="input"
                          data-testid={`property-editor-field-${constraint}-${index}`}
                          onChange={(event) =>
                            updateField(index, {
                              [constraint]: event.target.value === '' ? undefined : kind === 'number' ? Number(event.target.value) : event.target.value,
                            })
                          }
                        />
                      </span>
                    ))}
                    {!readOnly && (
                      <button
                        type="button"
                        className="prop-editor__icon"
                        title={t(`${KEY}.removeThisField`)}
                        aria-label={t(`${KEY}.removeThisField`)}
                        data-datum="props.remove-this-field"
                        data-kind="button"
                        data-testid={`property-editor-field-remove-${index}`}
                        onClick={() =>
                          writeFieldsNow(
                            jsonSchema?.title as string | undefined,
                            fields.filter((_, i) => i !== index)
                          )
                        }
                      >
                        <i className="pi pi-times" aria-hidden="true" />
                      </button>
                    )}
                  </div>
                  {struct.note && (
                    <span className="prop-editor__hint" data-datum="props.st-note" data-kind="hint">
                      {t(`${KEY}.structNote.${struct.id}`)}
                    </span>
                  )}
                </div>
              );
            })}
            {!readOnly && (
              <button
                type="button"
                className="prop-editor__quiet"
                data-datum="props.add-a-field"
                data-kind="button"
                data-testid="property-editor-add-field"
                onClick={() => writeFieldsNow(jsonSchema?.title as string | undefined, [...fields, { name: '', type: 'string', required: false }])}
              >
                <i className="pi pi-plus" aria-hidden="true" />
                {t(`${KEY}.addAField`)}
              </button>
            )}
            {!fields.length && (
              <p className="prop-editor__note" data-datum="props.a-structured-value-needs-a-shape-otherwi" data-kind="note">
                {t(`${KEY}.shapeEmpty`)}
              </p>
            )}
          </div>,
          'property-editor-shape'
        )}

      {property.type === 'array' && (
        <p className="prop-editor__note" data-datum="props.a-list-holds-simple-values-the-platform-" data-kind="note" data-testid="property-editor-list-note">
          {t(`${KEY}.listNote`)}
        </p>
      )}

      {property.type === 'string' &&
        section(
          'props.label#7',
          t(`${KEY}.choices`),
          t(`${KEY}.choicesNote`),
          <div className="prop-editor__opts">
            {options.map((option, index) => (
              <span className="prop-editor__chip" key={option.value ?? index} data-container="each(options)" data-datum="props.o-label#2" data-kind="chip">
                {option.label}
                {!readOnly && (
                  <button
                    type="button"
                    title={t(`${KEY}.removeChoice`, { label: option.label })}
                    aria-label={t(`${KEY}.removeChoice`, { label: option.label })}
                    data-datum="props.button#4"
                    data-kind="button"
                    data-testid={`property-editor-choice-remove-${index}`}
                    onClick={() => {
                      setMeta({ options: options.filter((_, i) => i !== index) });
                      commit();
                    }}
                  >
                    <i className="pi pi-times" aria-hidden="true" />
                  </button>
                )}
              </span>
            ))}
            {!readOnly && (
              <InputAtom
                className="prop-editor__input"
                placeholder={t(`${KEY}.addAChoice`)}
                aria-label={t(`${KEY}.addAChoice`)}
                data-datum="props.add-a-choice-then-enter"
                data-kind="input"
                data-testid="property-editor-add-choice"
                onKeyDown={(event) => {
                  const input = event.target as HTMLInputElement;
                  if (event.key !== 'Enter' || !input.value.trim()) return;
                  const label = input.value.trim();
                  setMeta({ options: [...options, { label, value: machineKey(label) }] });
                  commit();
                  input.value = '';
                }}
              />
            )}
          </div>,
          'property-editor-choices'
        )}

      {property.type === 'unit' &&
        section(
          'props.label#8',
          t(`${KEY}.units`),
          undefined,
          <>
            <div className="prop-editor__row">
              <label className="prop-editor__label" htmlFor={fieldId('property-editor-unit-category')} data-datum="props.measures" data-kind="label">
                {t(`${KEY}.measures`)}
              </label>
              {select(
                'props.select#6',
                unit.category ?? '',
                t(`${KEY}.measures`),
                [{ id: '', label: t(`${KEY}.chooseOne`) }, ...UNIT_CATEGORIES.map((id) => ({ id, label: t(`${KEY}.unitCategory.${id}`) }))],
                (value) => setUnit({ category: value || undefined }),
                'property-editor-unit-category'
              )}
              <span className="prop-editor__hint" data-datum="props.the-family-the-units-belong-to-required-" data-kind="hint">
                {t(`${KEY}.measuresHint`)}
              </span>
            </div>
            {!unit.category && (
              <p className="prop-editor__note" data-datum="props.without-a-family-a-value-can-hold-a-numb" data-kind="note">
                {t(`${KEY}.noFamilyNote`)}
              </p>
            )}
            <div className="prop-editor__row">
              <label className="prop-editor__label" htmlFor={fieldId('property-editor-allowed-units')} data-datum="props.allowed" data-kind="label">
                {t(`${KEY}.allowed`)}
              </label>
              <InputAtom
                className="prop-editor__input"
                value={(unit.allowedUnits ?? []).join(', ')}
                readOnly={readOnly}
                placeholder={t(`${KEY}.allowedPlaceholder`)}
                aria-label={t(`${KEY}.allowed`)}
                data-datum="props.km-mi"
                onBlur={commit}
                data-kind="input"
                data-testid="property-editor-allowed-units"
                id={fieldId('property-editor-allowed-units')}
                onChange={(event) =>
                  setUnit({
                    allowedUnits: event.target.value
                      .split(',')
                      .map((value) => value.trim())
                      .filter(Boolean),
                  })
                }
              />
              <label className="prop-editor__label" htmlFor={fieldId('property-editor-default-unit')} data-datum="props.default" data-kind="label">
                {t(`${KEY}.defaultUnit`)}
              </label>
              <InputAtom
                className="prop-editor__input prop-editor__input--narrow"
                value={unit.defaultUnit ?? ''}
                readOnly={readOnly}
                aria-label={t(`${KEY}.defaultUnit`)}
                data-datum="props.input#3"
                onBlur={commit}
                data-kind="input"
                data-testid="property-editor-default-unit"
                id={fieldId('property-editor-default-unit')}
                onChange={(event) => setUnit({ defaultUnit: event.target.value })}
              />
              <span className="prop-editor__hint" data-datum="props.leave-allowed-empty-for-every-unit-in-th" data-kind="hint">
                {t(`${KEY}.allowedHint`)}
              </span>
            </div>
            <div className="prop-editor__row">
              <label className="prop-editor__label" htmlFor={fieldId('property-editor-display-precision')} data-datum="props.precision" data-kind="label">
                {t(`${KEY}.precision`)}
              </label>
              <span className="prop-editor__num">
                <InputAtom
                  className="prop-editor__input prop-editor__input--narrow"
                  value={String(unit.displayPrecision ?? '2')}
                  disabled={readOnly}
                  inputMode="numeric"
                  aria-label={t(`${KEY}.displayPrecision`)}
                  data-datum="props.input#4"
                  onBlur={commit}
                  data-kind="input"
                  data-testid="property-editor-display-precision"
                  id={fieldId('property-editor-display-precision')}
                  onChange={(event) => setUnit({ displayPrecision: Number(event.target.value) })}
                />
              </span>
              <span className="prop-editor__hint" data-datum="props.shown" data-kind="hint">
                {t(`${KEY}.shown`)}
              </span>
              <span className="prop-editor__num">
                <InputAtom
                  className="prop-editor__input prop-editor__input--narrow"
                  value={String(unit.inputPrecision ?? '')}
                  disabled={readOnly}
                  inputMode="numeric"
                  aria-label={t(`${KEY}.inputPrecision`)}
                  data-datum="props.input#6"
                  onBlur={commit}
                  data-kind="input"
                  data-testid="property-editor-input-precision"
                  onChange={(event) =>
                    setUnit({
                      inputPrecision: event.target.value === '' ? undefined : Number(event.target.value),
                    })
                  }
                />
              </span>
              <span className="prop-editor__hint" data-datum="props.entered-leave-empty-to-follow-the-shown-" data-kind="hint">
                {t(`${KEY}.entered`)}
              </span>
            </div>
            <div className="prop-editor__row">
              {check(
                'props.let-people-switch-unit',
                'props.checkbox#2',
                t(`${KEY}.letPeopleSwitchUnit`),
                unit.showSwitcher !== false,
                (checked) => setUnit({ showSwitcher: checked }),
                'property-editor-show-switcher'
              )}
            </div>
          </>,
          'property-editor-units'
        )}

      {section(
        'props.label#9',
        t(`${KEY}.howItBehaves`),
        undefined,
        <>
          <div className="prop-editor__row">
            {check(
              'props.required#3',
              'props.checkbox#3',
              t(`${KEY}.required`),
              !!property.required,
              (checked) => set({ required: checked }),
              'property-editor-required'
            )}
            {check(
              'props.read-only',
              'props.checkbox#4',
              t(`${KEY}.readOnlyFlag`),
              !!property.read_only,
              (checked) => set({ read_only: checked }),
              'property-editor-read-only'
            )}
            {/*
              Immutable is the one flag that cannot be undone for a sphere, so it names its
              consequence rather than sitting as a bare checkbox.
            */}
            {check(
              'props.settable-once',
              'props.checkbox#5',
              t(`${KEY}.settableOnce`),
              !!property.immutable,
              (checked) => set({ immutable: checked }),
              'property-editor-immutable'
            )}
            {check(
              'props.hidden-by-default',
              'props.checkbox#6',
              t(`${KEY}.hiddenByDefault`),
              !!property.hidden,
              (checked) => set({ hidden: checked }),
              'property-editor-hidden'
            )}
          </div>
          {property.immutable && (
            <p className="prop-editor__note" data-datum="props.once-a-sphere-has-a-value-it-can-never-b" data-kind="note">
              {t(`${KEY}.immutableNote`)}
            </p>
          )}
          <div className="prop-editor__row">
            <label className="prop-editor__label" htmlFor={fieldId('property-editor-default-value')} data-datum="props.starts-as" data-kind="label">
              {t(`${KEY}.startsAs`)}
            </label>
            <InputAtom
              className="prop-editor__input"
              value={property.default_value ?? ''}
              readOnly={readOnly}
              placeholder={t(`${KEY}.startsAsPlaceholder`)}
              aria-label={t(`${KEY}.startsAs`)}
              data-datum="props.empty"
              onBlur={commit}
              data-kind="input"
              data-testid="property-editor-default-value"
              id={fieldId('property-editor-default-value')}
              onChange={(event) => set({ default_value: event.target.value })}
            />
            <span className="prop-editor__hint" data-datum="props.the-value-a-new-sphere-carries-before-an" data-kind="hint">
              {t(`${KEY}.startsAsHint`)}
            </span>
          </div>
          <div className="prop-editor__row">
            {check(
              'props.no-two-spheres-share-a-value',
              'props.checkbox#7',
              t(`${KEY}.unique`),
              !!property.unique,
              (checked) => set({ unique: checked }),
              'property-editor-unique'
            )}
            {property.unique && (
              <>
                <span className="prop-editor__label" data-datum="props.within" data-kind="label">
                  {t(`${KEY}.within`)}
                </span>
                <SegmentedControlAtom
                  mode="single"
                  size="small"
                  renderAs="button"
                  optionKind="button"
                  ariaLabel={t(`${KEY}.within`)}
                  value={property.uniqueScope ?? 'schema'}
                  options={(['schema', 'tenant', 'global'] as UniqueScope[]).map((scope) => ({
                    value: scope,
                    label: t(`${KEY}.uniqueScope.${scope}`),
                    datum: 'props.o-label#3',
                    disabled: readOnly,
                  }))}
                  onChange={(value) => setNow({ uniqueScope: value as UniqueScope })}
                  data-testid="property-editor-unique-scope"
                />
              </>
            )}
          </div>
        </>,
        'property-editor-how-it-behaves'
      )}

      {section(
        'props.label#10',
        t(`${KEY}.checks`),
        undefined,
        <div className="prop-editor__rules" data-testid="property-editor-checks">
          {rules.map((rule, index) => {
            const def = ruleDef(rule.method);
            const stale = !usable.some((method) => method.id === rule.method);
            return (
              <div className="prop-editor__rule" key={index} data-container="each(rules)" data-bad={stale ? '1' : undefined}>
                {select(
                  'props.select#7',
                  rule.method,
                  t(`${KEY}.checkMethod`),
                  usable.map((method) => ({
                    id: method.id,
                    label: t(`${KEY}.ruleMethod.${method.id}`),
                  })),
                  (value) => updateRule(index, { method: value, args: [] }),
                  `property-editor-rule-method-${index}`
                )}
                {def?.arg && (
                  <InputAtom
                    className="prop-editor__input prop-editor__input--narrow"
                    value={fromArgs(rule.method, rule.args)}
                    readOnly={readOnly}
                    placeholder={t(`${KEY}.ruleArgPlaceholder.${def.arg}`)}
                    aria-label={t(`${KEY}.ruleArg`)}
                    data-datum="props.input#8"
                    onBlur={commit}
                    data-kind="input"
                    data-testid={`property-editor-rule-arg-${index}`}
                    onChange={(event) => updateRule(index, { args: toArgs(rule.method, event.target.value) })}
                  />
                )}
                <InputAtom
                  className="prop-editor__input prop-editor__input--wide"
                  value={rule.message ?? ''}
                  readOnly={readOnly}
                  placeholder={t(`${KEY}.ruleMessagePlaceholder`)}
                  aria-label={t(`${KEY}.ruleMessage`)}
                  data-datum="props.what-to-tell-someone-who-breaks-it"
                  onBlur={commit}
                  data-kind="input"
                  data-testid={`property-editor-rule-message-${index}`}
                  onChange={(event) => updateRule(index, { message: event.target.value })}
                />
                {!readOnly && (
                  <button
                    type="button"
                    className="prop-editor__icon"
                    title={t(`${KEY}.removeThisCheck`)}
                    aria-label={t(`${KEY}.removeThisCheck`)}
                    data-datum="props.remove-this-check"
                    data-kind="button"
                    data-testid={`property-editor-rule-remove-${index}`}
                    onClick={() => setNow({ rules: rules.filter((_, i) => i !== index) })}
                  >
                    <i className="pi pi-times" aria-hidden="true" />
                  </button>
                )}
              </div>
            );
          })}
          {hasStaleRule && (
            <p className="prop-editor__note" data-datum="props.a-check-above-doesn-t-apply-to" data-kind="note" data-testid="property-editor-stale-check">
              {t(`${KEY}.staleCheck`, { type: typeLabel(property.type) })}
            </p>
          )}
          {!readOnly && (
            <button
              type="button"
              className="prop-editor__quiet"
              data-datum="props.add-a-check"
              data-kind="button"
              data-testid="property-editor-add-check"
              onClick={() => setNow({ rules: [...rules, { method: usable[0]?.id ?? '', args: [], message: '' }] })}
            >
              <i className="pi pi-plus" aria-hidden="true" />
              {t(`${KEY}.addACheck`)}
            </button>
          )}
          {!rules.length && (
            <span className="prop-editor__hint" data-datum="props.checks-here-read-this-property-only-anyt" data-kind="hint">
              {t(`${KEY}.checksHint`)}
            </span>
          )}
        </div>,
        'property-editor-checks-section'
      )}

      {section(
        'props.label#11',
        t(`${KEY}.howItRenders`),
        undefined,
        <>
          {property.type === 'json' && (
            <p className="prop-editor__note" data-datum="props.a-structured-value-renders-as-a-form-bui" data-kind="note">
              {t(`${KEY}.structuredRendersNote`)}
            </p>
          )}
          <div className="prop-editor__row">
            <label className="prop-editor__label" htmlFor={fieldId('property-editor-renderer-edit')} data-datum="props.editing" data-kind="label">
              {t(`${KEY}.editing`)}
            </label>
            {select(
              'props.select#8',
              property.renderers?.edit?.name ?? '',
              t(`${KEY}.editing`),
              ['', ...renderers].map((id) => ({ id, label: id || t(`${KEY}.defaultForTheType`) })),
              (value) => set({ renderers: rendererMapWith('edit', value) }),
              'property-editor-renderer-edit'
            )}
            <label className="prop-editor__label" htmlFor={fieldId('property-editor-renderer-display')} data-datum="props.reading" data-kind="label">
              {t(`${KEY}.reading`)}
            </label>
            {select(
              'props.select#9',
              property.renderers?.view?.name ?? '',
              t(`${KEY}.reading`),
              ['', ...renderers].map((id) => ({ id, label: id || t(`${KEY}.defaultForTheType`) })),
              (value) => set({ renderers: rendererMapWith('view', value) }),
              'property-editor-renderer-display'
            )}
          </div>
          {/*
            `dock` is a real switch with a concrete effect — the sphere editor collects docked
            properties and gives each its own tab, and the properties list shows a summary
            instead of an editor. The label names what happens, not the mechanism.
          */}
          <div className="prop-editor__row">
            {check(
              'props.opens-in-its-own-tab',
              'props.checkbox#8',
              t(`${KEY}.opensInItsOwnTab`),
              !!property.dock,
              (checked) => set({ dock: checked }),
              'property-editor-dock'
            )}
            <span className="prop-editor__hint" data-datum="props.for-a-value-whose-editor-is-too-big-for-" data-kind="hint">
              {t(`${KEY}.dockHint`)}
            </span>
          </div>
          {property.dock && (
            <p className="prop-editor__note" data-datum="props.on-a-sphere-it-gets-a-tab-of-its-own-the" data-kind="note">
              {t(`${KEY}.dockNote`)}
            </p>
          )}
        </>,
        'property-editor-how-it-renders'
      )}

      {section(
        'props.label#12',
        t(`${KEY}.whoCanSeeIt`),
        undefined,
        <>
          <div className="prop-editor__row">
            <label className="prop-editor__label" htmlFor={fieldId('property-editor-access-read')} data-datum="props.to-read" data-kind="label">
              {t(`${KEY}.toRead`)}
            </label>
            {select(
              'props.select#10',
              property.access?.read ?? '',
              t(`${KEY}.toRead`),
              ['', ...PERMS].map((id) => ({ id, label: id || t(`${KEY}.sameAsTheSphere`) })),
              (value) =>
                set({
                  access: {
                    ...property.access,
                    read: (value || undefined) as SchemaPropertyPermission,
                  },
                }),
              'property-editor-access-read'
            )}
            <label className="prop-editor__label" htmlFor={fieldId('property-editor-access-write')} data-datum="props.to-write" data-kind="label">
              {t(`${KEY}.toWrite`)}
            </label>
            {select(
              'props.select#11',
              property.access?.write ?? '',
              t(`${KEY}.toWrite`),
              ['', ...PERMS].map((id) => ({ id, label: id || t(`${KEY}.sameAsTheSphere`) })),
              (value) =>
                set({
                  access: {
                    ...property.access,
                    write: (value || undefined) as SchemaPropertyPermission,
                  },
                }),
              'property-editor-access-write'
            )}
          </div>
          <p className="prop-editor__note" data-datum="props.left-alone-the-property-is-as-visible-as" data-kind="note">
            {t(`${KEY}.accessNote`)}
          </p>
        </>,
        'property-editor-who-can-see-it'
      )}
    </div>
  );
};

export default PropertyEditorMolecule;
